import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import OniiSyncButton from "../components/OniiSyncButton";
import { 
  Upload, 
  ShoppingCart, 
  TrendingUp,
  Search,
  Calendar
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [salesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/sales/analytics`)
      ]);
      setSales(salesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Arquivo inválido", {
        description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)"
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/sales/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Relatório importado!", {
        description: `${res.data.sales_added} vendas adicionadas. Estoque atualizado para ${res.data.stock_updated} produtos.`
      });
      
      fetchData();
    } catch (error) {
      toast.error("Erro ao importar", {
        description: error.response?.data?.detail || "Erro ao processar arquivo"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const filteredSales = sales.filter(s => 
    s.item_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.ean?.includes(search) ||
    s.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group sales by transaction for display
  const uniqueTransactions = [...new Set(sales.map(s => s.transaction_id))].length;

  return (
    <div className="p-8 animate-fadeIn" data-testid="sales-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vendas</h1>
          <p className="text-muted-foreground">Importe relatórios e acompanhe as vendas</p>
        </div>
        <OniiSyncButton onImportComplete={fetchData} />
      </div>

      {/* Upload Area */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div
            className={`upload-zone ${dragOver ? 'dragover' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('sales-file-input').click()}
            data-testid="sales-upload-zone"
          >
            <input
              id="sales-file-input"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files[0])}
            />
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-lg font-medium mb-1">
              {uploading ? "Importando..." : "Arraste o relatório de vendas aqui"}
            </p>
            <p className="text-sm text-muted-foreground">
              Arquivo Excel (.xlsx) no formato de transações
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Total Vendas</p>
            <p className="kpi-value">{sales.length}</p>
            <p className="text-xs text-muted-foreground mt-1">itens vendidos</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Transações</p>
            <p className="kpi-value">{uniqueTransactions}</p>
            <p className="text-xs text-muted-foreground mt-1">únicas</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Receita Total</p>
            <p className="kpi-value text-2xl">{formatCurrency(analytics?.total_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Produtos Vendidos</p>
            <p className="kpi-value">{analytics?.total_products_sold || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">unidades</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Products Chart */}
      {analytics?.top_products?.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
              Ritmo de Vendas - Top 15 Produtos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={analytics.top_products.slice(0, 15)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={200}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value.length > 30 ? value.slice(0, 30) + '...' : value}
                />
                <Tooltip 
                  contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}
                  formatter={(value, name) => {
                    if (name === 'total_qty') return [value, 'Quantidade'];
                    if (name === 'total_revenue') return [formatCurrency(value), 'Receita'];
                    return [value, name];
                  }}
                />
                <Bar dataKey="total_qty" fill="hsl(143 49% 20%)" radius={[0, 4, 4, 0]} name="total_qty" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Sales Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
              Histórico de Vendas
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="pl-10"
                data-testid="sales-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : filteredSales.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="data-table" data-testid="sales-table">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th>Data</th>
                    <th>Produto</th>
                    <th>EAN</th>
                    <th>Qtd</th>
                    <th>Preço Un.</th>
                    <th>Total</th>
                    <th>Cliente</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.slice(0, 100).map((sale, idx) => (
                    <tr key={`${sale.id}-${idx}`}>
                      <td className="text-xs whitespace-nowrap">{formatDate(sale.date)}</td>
                      <td className="max-w-[200px] truncate" title={sale.item_name}>
                        {sale.item_name}
                      </td>
                      <td className="font-mono text-xs">{sale.ean}</td>
                      <td className="font-mono font-medium">{sale.quantity}</td>
                      <td className="font-mono">{formatCurrency(sale.sale_price)}</td>
                      <td className="font-mono font-medium">{formatCurrency(sale.total_item)}</td>
                      <td className="text-xs max-w-[120px] truncate" title={sale.customer_name}>
                        {sale.customer_name?.replace(/\s+/g, ' ').trim() || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredSales.length > 100 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  Mostrando 100 de {filteredSales.length} vendas
                </p>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma venda registrada</p>
              <p className="text-sm">Importe um relatório de vendas para começar</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Sales;
